import React, { useCallback } from 'react'
import moment, { Moment } from 'moment/moment';

import ChevronIcon from './icons/ChevronIcon';
import { Form } from 'informed';
import TyDatePicker from '../../../components/inputs/TyDatePicker';
import { ShiftFilterOptions } from './ShiftFilter';
import { useQuery } from 'react-query';
import apiClient from '../../../apiClient';
import useSearchString from '../../../hooks/UrlParamsHandler/useSearchParams';
import { ShiftData } from '@tymbe/schema/src/shift.interface';
import { FavouriteIcon } from '@tymbe/ui-icons';
import classNames from 'classnames';

const DateHandlingBar = () => {
  const now = moment().startOf('week').toISOString();
  const [searchParams, setSearchParams] = useSearchString();
  const start = searchParams.get('start') ?? now;
  const companyId = Number(searchParams.get('company_id')) || undefined;
  const branchofficeId = Number(searchParams.get('branchoffice_id')) || undefined;
  const departmentId = Number(searchParams.get('department_id')) || undefined;
  const shiftFilter = searchParams.get('shiftFilter') || undefined;
  const templates = searchParams.get('templates')?.split(',').map(Number) || [];

  const getMonthTitle = (s: string, e?: string) => {
    const startDate = moment(s);
    const endDate = e ? moment(e) : startDate;
    return startDate.get('month') === endDate.get('month')
      ? startDate.format('MMMM')
      : `${startDate.format('MMM')} - ${endDate.format('MMM')}`;
  };

  const filters = {
    templates,
    companyId,
    branchofficeId,
    departmentId,
    shiftFilter,
  };

  const {data: shiftDatesWithoutAttendance} = useQuery(['unresolved', 'calendar', filters], async (): Promise<any> => {
    const modifiers: Record<string, boolean> = {daysWithUnconfirmedAttendance: true};
    if (filters.shiftFilter === ShiftFilterOptions.NOT_FULL) modifiers.unFilledOnly = true;

    const query = {
      'shift.company_id': filters.companyId,
      parent_id: filters.branchofficeId,
      branchoffice_id: filters.departmentId,
      $limit: -1,
      $modify: modifiers,
      $eager: '[shiftTemplate]',
      $joinRelation: 'branchoffice',
      shift_template_id: {},
    };
    if (filters.templates?.length) {
      query.shift_template_id = { $in: filters.templates };
    }
    return apiClient.service('shift').find({ query });
  }, { staleTime: 1000 * 5});

    const isBefore = shiftDatesWithoutAttendance?.find((shft: ShiftData & {shift_date: string}) => moment(shft.shift_date).isBefore(start))
    const isAfter = shiftDatesWithoutAttendance?.find((shft: ShiftData & {shift_date: string}) => moment(shft.shift_date).isAfter(moment(start).add(7, 'day').toISOString()))

  const handlePrevWeek = useCallback(() => {
    const newStart = moment(start).subtract(1, 'week').toISOString();
    const newParams = new URLSearchParams(searchParams);
    if (newStart === now) {
      newParams.delete('start');
    } else {
      newParams.set('start', newStart);
    }
    setSearchParams(newParams);
  }, [now, searchParams, setSearchParams, start]);

  const handleNextWeek = useCallback(() => {
    const newStart = moment(start).add(1, 'week').toISOString();
    const newParams = new URLSearchParams(searchParams);
    if (newStart === now) {
      newParams.delete('start');
    } else {
      newParams.set('start', newStart);
    }
    setSearchParams(newParams);
  }, [now, searchParams, setSearchParams, start]);

  const setWeek = useCallback((date: Moment) => {
    const newStart = date.startOf('week').toISOString();
    const newParams = new URLSearchParams(searchParams);
    if (newStart === now) {
      newParams.delete('start');
    } else {
      newParams.set('start', newStart);
    }
    setSearchParams(newParams);
  }, [now, searchParams, setSearchParams]);

  return (
    <div className='flex gap-3 bg-white h-10'>
      <div className="capitalize shadow-down-xs flex rounded-lg border border-secondary-100 w-fit">
        <button
          className={classNames("relative flex p-1 justify-center items-center size-10 rounded-none",
            "border-r border-secondary-100 hover:bg-secondary-50"
          )}
          type="button"
          onClick={handlePrevWeek}>
            <ChevronIcon className='-scale-x-100' />
            {isBefore &&
              <FavouriteIcon name="error" className='size-4 absolute -top-1 -left-1 text-red-500' />
            }
        </button>
          <Form
            onChange={({ values }) => {
              if (values.date) setWeek(values.date);
            }}
            className='flex items-center w-52 hover:bg-secondary-50'
          >
            <TyDatePicker
              name="date"
              datePresets={[
                {
                  label: 'Tento týden',
                  date: moment().startOf('week').toISOString(),
                },
              ]}
              defaultValue={moment(start)}
              // there is no easy way to extract input render
              // eslint-disable-next-line react/no-unstable-nested-components
              inputRender={(props) => (
                <label className="text-body-2-xl font-semibold text-secondary-600 flex gap-2.5 px-3 items-center">
                  <FavouriteIcon name="calendar_today" className='size-4' />
                  {getMonthTitle(
                    start,
                    moment(start).add(7, 'day').toISOString(),
                  )}
                  {moment(start).format(' YYYY')}
                  <input hidden {...props} />
                </label>
              )}
            />
          </Form>
          <button
            className={classNames("relative flex p-1 justify-center items-center size-10",
              "rounded-none border-l border-secondary-100 hover:bg-secondary-50"
            )}
            type="button"
            onClick={handleNextWeek}>
            <ChevronIcon />
            {isAfter &&
              <FavouriteIcon name="error" className='size-4 absolute -top-1 -right-1 text-red-500' />
              }
          </button>
        </div>
        <button
          onClick={() => setWeek(moment().startOf('week'))}
          type='button'
          className={classNames("text-body-2-xl font-semibold flex shadow-down-xs rounded-lg",
          "border border-secondary-100 items-center justify-center px-3 hover:bg-secondary-50"
          )}>
            Dnes
        </button>
      </div>
  )
}

export default DateHandlingBar
